import { useCallback } from "react";
import { fetchConversation } from "@/features/intelligence/api/client";
import {
  ACTIVE_TURN_DELAYS_MS,
  announceDocumentHeal,
  documentHealFrame,
  DOCUMENT_HEAL_DELAYS_MS,
  endAllCalls,
  hydrateThread,
  recordDocumentHeal,
  resolvedByDocument,
  shouldAwaitActiveTurn,
  shouldReadDocumentAgain,
  unresolvedTools,
  type DocumentReadBack,
  type ThreadState,
} from "@/features/intelligence/lib/thread-state";
import { forgetRun, inViewEntities, sleep } from "./runtime";
import type { ApplyThread, MutableRef, StateSetter } from "./types";

export function useDocumentHealing({
  activeIdRef,
  busyRef,
  threadRef,
  frontendContextRef,
  setCanReconnect,
  applyThread,
}: {
  activeIdRef: MutableRef<string | null>;
  busyRef: MutableRef<boolean>;
  threadRef: MutableRef<ThreadState>;
  frontendContextRef: MutableRef<object | null | undefined>;
  setCanReconnect: StateSetter<boolean>;
  applyThread: ApplyThread;
}) {
  const readBackDocument = useCallback(
    async (id: string, turnRunId: string | null): Promise<DocumentReadBack | null> => {
      if (activeIdRef.current !== id || busyRef.current) return null;
      const reread = await fetchConversation(id);
      if (!reread.ok || activeIdRef.current !== id || busyRef.current) return null;

      const carried = threadRef.current;
      const hydrated = hydrateThread(reread.value);
      const settled = reread.value.activeTurn === null;
      const lastReply = [...hydrated.nodes].reverse().find((node) => node.role === "assistant");
      const resolved =
        carried.outcome !== "running" && carried.outcome !== "interrupted"
          ? carried.outcome
          : settled
            ? lastReply === undefined
              ? "idle"
              : lastReply.truncated
                ? "truncated"
                : lastReply.errored
                  ? "errored"
                  : "complete"
            : "interrupted";
      if (settled) {
        setCanReconnect(false);
        forgetRun(id);
      } else if (resolved === "interrupted" && carried.runId) {
        setCanReconnect(true);
      }
      const read = resolved === "interrupted" ? hydrated : endAllCalls(hydrated);
      const healed = resolvedByDocument(carried, read, turnRunId);
      const healFrame = documentHealFrame();
      applyThread(() =>
        announceDocumentHeal(
          recordDocumentHeal(
            {
              ...read,
              outcome: resolved,
              cancelled: carried.cancelled,
              notice: carried.notice,
              activeConfirmed: resolved === "interrupted",
              runError: carried.runError,
              lastEventId: carried.lastEventId,
              runId: settled ? null : carried.runId,
              invalidated: carried.invalidated,
              toolStartedAt: carried.toolStartedAt,
              healedTools: carried.healedTools,
            },
            healed,
          ),
          healFrame,
          healed.length > 0 ? inViewEntities(frontendContextRef.current) : [],
        ),
      );
      return {
        settled,
        cancelled: carried.cancelled || resolved === "truncated",
        unresolved: unresolvedTools(hydrated, turnRunId).length,
      };
    },
    [activeIdRef, applyThread, busyRef, frontendContextRef, setCanReconnect, threadRef],
  );

  const healAfterStream = useCallback(
    async (id: string, turnRunId: string | null): Promise<DocumentReadBack | null> => {
      let seen = await readBackDocument(id, turnRunId);
      for (const delay of ACTIVE_TURN_DELAYS_MS) {
        if (!shouldAwaitActiveTurn(seen)) break;
        await sleep(delay);
        if (activeIdRef.current !== id || busyRef.current) return seen;
        const next = await readBackDocument(id, turnRunId);
        if (next === null) break;
        seen = next;
      }
      for (const delay of DOCUMENT_HEAL_DELAYS_MS) {
        if (!shouldReadDocumentAgain(seen)) break;
        await sleep(delay);
        if (activeIdRef.current !== id || busyRef.current) return seen;
        seen = await readBackDocument(id, turnRunId);
      }
      return seen;
    },
    [activeIdRef, busyRef, readBackDocument],
  );

  return { healAfterStream };
}
